import { createFileRoute, Link } from '@tanstack/react-router';

export const Route = createFileRoute('/services/digital-help')({
  component: DigitalHelpPage,
  head: () => ({
    meta: [
      { title: 'Getting online, staying safe — digital help — Warren-Blackwood' },
      {
        name: 'description',
        content:
          'Free help to get online and stay safe: Be Connected for older Australians, library computer classes across Warren-Blackwood, and the eSafety Commissioner for scams, abuse and online safety.',
      },
      { property: 'og:title', content: 'Getting online, staying safe — Warren-Blackwood' },
      {
        property: 'og:description',
        content:
          'Free digital literacy programs, local library classes, and where to report online abuse and scams.',
      },
    ],
  }),
});

type Phone = { label: string; tel: string; note?: string };
type Block = {
  agency: string;
  what: string;
  phone?: Phone[];
  website?: { label: string; href: string };
  tips?: string[];
};

const SECTIONS: { id: string; title: string; blurb: string; items: Block[] }[] = [
  {
    id: 'be-connected',
    title: 'Be Connected — free for older Australians',
    blurb:
      'Be Connected is a federal program designed for people aged 50 and over who want to build confidence online. The website has free, plain-English lessons you can do at your own pace; the network of local partners (libraries, CRCs, neighbourhood centres) runs in-person sessions.',
    items: [
      {
        agency: 'Be Connected (eSafety Commissioner)',
        what: 'Free online courses covering smartphones, email, online banking, video calls, scams and social media. Start with the basics and skip ahead when you are ready.',
        phone: [{ label: 'eSafety enquiries', tel: '1800880176', note: '1800 880 176' }],
        website: { label: 'beconnected.esafety.gov.au', href: 'https://beconnected.esafety.gov.au/' },
        tips: [
          'You can do the courses at home, or sit beside a friend or library volunteer who can help.',
          'Courses are free \u2014 there is no subscription and no payment ever required.',
        ],
      },
      {
        agency: 'Find a Network Partner near you',
        what: 'Be Connected funds local organisations to run one-on-one and small-group sessions. Several libraries and Community Resource Centres across Warren-Blackwood take part.',
        website: { label: 'Find a Be Connected partner', href: 'https://beconnected.esafety.gov.au/network-partners' },
      },
    ],
  },
  {
    id: 'libraries',
    title: 'Local library computer help',
    blurb:
      'Public libraries across the six shires offer free computer use, free WiFi, and informal help from staff with myGov, Centrelink, printing, scanning and basic device questions. Some run scheduled classes; for one-off help, ring ahead and book a time.',
    items: [
      {
        agency: 'Margaret River Library',
        what: 'Computers, WiFi, printing; staff help with myGov, email, basic device questions.',
        phone: [{ label: 'Library', tel: '0897805611', note: '(08) 9780 5611' }],
        website: { label: 'amrshire.wa.gov.au libraries', href: 'https://www.amrshire.wa.gov.au/services/libraries' },
      },
      {
        agency: 'Manjimup Library',
        what: 'Computers, WiFi, printing; community classes from time to time \u2014 ask at the desk.',
        phone: [{ label: 'Library', tel: '0897711166', note: '(08) 9771 1166' }],
        website: { label: 'manjimup.wa.gov.au libraries', href: 'https://www.manjimup.wa.gov.au/our-shire/services-facilities/libraries' },
      },
      {
        agency: 'Bridgetown Library',
        what: 'Computers, WiFi, printing; quiet space for video appointments.',
        phone: [{ label: 'Library', tel: '0897612063', note: '(08) 9761 2063' }],
        website: { label: 'bridgetown.wa.gov.au library', href: 'https://www.bridgetown.wa.gov.au/' },
      },
      {
        agency: 'Denmark Library',
        what: 'Computers, WiFi, printing; helpful first stop in a part of the electorate with patchy home internet.',
        phone: [{ label: 'Shire of Denmark', tel: '0898480300', note: '(08) 9848 0300' }],
        website: { label: 'denmark.wa.gov.au library', href: 'https://www.denmark.wa.gov.au/' },
      },
      {
        agency: 'Boyup Brook & Nannup CRCs',
        what: 'Community Resource Centres in smaller towns provide computer access, printing and help with online forms.',
        website: { label: 'CRC network', href: 'https://www.crcnetwork.com.au/' },
      },
    ],
  },
  {
    id: 'safety',
    title: 'Staying safe online',
    blurb:
      'The eSafety Commissioner is the national regulator for online safety. They handle reports of cyberbullying, image-based abuse, illegal content and adult cyber abuse \u2014 and have plain-English guides for older people, parents and small businesses.',
    items: [
      {
        agency: 'eSafety Commissioner',
        what: 'Report cyberbullying, image-based abuse (intimate images shared without consent), serious adult cyber abuse, and illegal or restricted content. Free advice and removal help.',
        phone: [{ label: 'Enquiries', tel: '1800880176', note: '1800 880 176' }],
        website: { label: 'esafety.gov.au', href: 'https://www.esafety.gov.au/' },
      },
      {
        agency: 'eSafety \u2014 for seniors',
        what: 'Dedicated section with advice on scams, romance fraud, privacy and online shopping safety.',
        website: { label: 'esafety.gov.au/seniors', href: 'https://www.esafety.gov.au/seniors' },
      },
      {
        agency: 'Scamwatch (ACCC) / National Anti-Scam Centre',
        what: 'Report a scam, check current scam warnings, get advice if you have lost money.',
        website: { label: 'scamwatch.gov.au', href: 'https://www.scamwatch.gov.au/' },
      },
      {
        agency: 'IDCARE \u2014 identity & cyber support',
        what: 'Free national service if your identity has been stolen or your accounts have been hacked.',
        phone: [{ label: 'Support line', tel: '1800595160', note: '1800 595 160' }],
        website: { label: 'idcare.org', href: 'https://www.idcare.org/' },
      },
    ],
  },
];

function DigitalHelpPage() {
  const h2: React.CSSProperties = {
    fontFamily: 'var(--font-heading)',
    fontSize: '1.4rem',
    color: 'var(--rivergum)',
    marginTop: 0,
    marginBottom: 'var(--sp-xs)',
    letterSpacing: '0.02em',
  };
  const p: React.CSSProperties = {
    color: 'var(--ink)',
    lineHeight: 1.6,
    marginBottom: 'var(--sp-sm)',
  };
  const link: React.CSSProperties = { color: 'var(--green-mid)', fontWeight: 600 };

  const card: React.CSSProperties = {
    background: 'var(--warm-white, #faf7f0)',
    border: '1px solid var(--light-border)',
    borderRadius: 10,
    padding: 'var(--sp-md)',
    marginBottom: 'var(--sp-md)',
  };
  const agencyBox: React.CSSProperties = {
    background: '#fff',
    border: '1px solid var(--light-border)',
    borderRadius: 8,
    padding: '0.75rem 0.9rem',
    marginTop: '0.6rem',
  };

  return (
    <main
      id="main-content"
      style={{
        maxWidth: 820,
        margin: '0 auto',
        padding: 'var(--sp-lg) var(--sp-md) var(--sp-xl)',
      }}
    >
      <p
        style={{
          textTransform: 'uppercase',
          letterSpacing: '0.12em',
          fontSize: '0.75rem',
          fontWeight: 700,
          color: 'var(--green-mid)',
          margin: 0,
          marginBottom: '0.35rem',
        }}
      >
        Services &middot; Digital help
      </p>
      <h1
        style={{
          fontFamily: 'var(--font-heading)',
          fontSize: 'clamp(2rem, 5vw, 2.75rem)',
          color: 'var(--green)',
          margin: 0,
          marginBottom: 'var(--sp-md)',
          lineHeight: 1.1,
        }}
      >
        Getting online, staying safe
      </h1>
      <p
        style={{
          color: 'var(--ink-mid)',
          fontStyle: 'italic',
          fontSize: '1.05rem',
          marginBottom: 'var(--sp-lg)',
          lineHeight: 1.5,
        }}
      >
        More and more government services \u2014 Centrelink, Medicare, ATO,
        your council \u2014 expect you to use them online. The good news: free
        help to learn at your own pace is closer than you think. Most of it is
        a phone call or a short drive to your local library away.
      </p>

      <div
        style={{
          background: '#eef5fb',
          border: '1px solid #b9d4e8',
          borderRadius: 8,
          padding: 'var(--sp-md)',
          marginBottom: 'var(--sp-lg)',
        }}
      >
        <strong style={{ color: '#1b4f72' }}>Three good starting points</strong>
        <p style={{ ...p, margin: '0.4rem 0 0', fontSize: '0.95rem' }}>
          1. <a href="https://beconnected.esafety.gov.au/" style={link}>Be Connected</a>{' '}
          \u2014 free online lessons for people 50+.<br />
          2. Your <Link to="/" style={link}>local library</Link> \u2014 free
          computers, WiFi, and a friendly hand with myGov.<br />
          3. <a href="https://www.esafety.gov.au/seniors" style={link}>eSafety for seniors</a>{' '}
          \u2014 spot scams and stay safe online.
        </p>
      </div>

      <nav
        aria-label="Topics on this page"
        style={{
          background: '#eef5fb',
          border: '1px solid #b9d4e8',
          borderRadius: 8,
          padding: 'var(--sp-md)',
          marginBottom: 'var(--sp-lg)',
        }}
      >
        <strong style={{ color: '#1b4f72' }}>Jump to a topic</strong>
        <ul
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            gap: '0.4rem 1rem',
            listStyle: 'none',
            padding: 0,
            margin: '0.6rem 0 0',
          }}
        >
          {SECTIONS.map((s) => (
            <li key={s.id}>
              <a href={`#${s.id}`} style={link}>
                {s.title}
              </a>
            </li>
          ))}
        </ul>
      </nav>

      {SECTIONS.map((topic) => (
        <section key={topic.id} id={topic.id} style={card}>
          <h2 style={h2}>{topic.title}</h2>
          <p style={{ ...p, marginBottom: 0 }}>{topic.blurb}</p>

          {topic.items.map((c, i) => (
            <div key={i} style={agencyBox}>
              <div
                style={{
                  fontWeight: 700,
                  color: 'var(--ink)',
                  marginBottom: '0.15rem',
                }}
              >
                {c.agency}
              </div>
              <div
                style={{
                  color: 'var(--ink-mid)',
                  fontSize: '0.95rem',
                  marginBottom: '0.4rem',
                  lineHeight: 1.5,
                }}
              >
                {c.what}
              </div>
              {c.phone && c.phone.length > 0 && (
                <div style={{ fontSize: '0.95rem', marginBottom: '0.25rem' }}>
                  {c.phone.map((ph, j) => (
                    <div key={j}>
                      <span style={{ color: 'var(--ink-mid)' }}>{ph.label}: </span>
                      <a href={`tel:${ph.tel}`} style={link}>
                        {ph.note ?? ph.tel}
                      </a>
                    </div>
                  ))}
                </div>
              )}
              {c.website && (
                <div style={{ fontSize: '0.95rem' }}>
                  <a href={c.website.href} style={link}>
                    {c.website.label} &rarr;
                  </a>
                </div>
              )}
              {c.tips && c.tips.length > 0 && (
                <ul
                  style={{
                    margin: '0.4rem 0 0',
                    paddingLeft: '1.1rem',
                    color: 'var(--ink-mid)',
                    fontSize: '0.9rem',
                    lineHeight: 1.5,
                  }}
                >
                  {c.tips.map((tip, k) => (
                    <li key={k}>{tip}</li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </section>
      ))}

      <div
        style={{
          background: '#fdf6e3',
          border: '1px solid #e8d9a8',
          borderRadius: 8,
          padding: 'var(--sp-md)',
          marginTop: 'var(--sp-lg)',
        }}
      >
        <strong style={{ color: '#7a5b1e' }}>Helping a family member?</strong>
        <p style={{ ...p, margin: '0.4rem 0 0', fontSize: '0.95rem' }}>
          Be Connected has a free guide for family and friends supporting an
          older person to get online without taking over. See also{' '}
          <Link to="/services/seniors" style={link}>Seniors services</Link>{' '}
          and <Link to="/services/who-do-i-call" style={link}>Who do I call?</Link>{' '}
          for the right agency contacts.
        </p>
      </div>
    </main>
  );
}
